// src/components/Popup/filterInventory.js
import React, { useEffect, useState } from 'react';
import {
    Drawer,
    Box,
    Typography,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Select,
    MenuItem,
    TextField,
    Button,
    Grid,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE_URL, API_INVENTORY } from '../../../helpers/urlHelper';

export default function FilterInventory({ open, onClose, onApply }) {
    const defaultFilters = {
        healthCenter: '',
        medicineCategory: '',
        stockStatus: 'all',
        expiryDate: 'all',
        supplier: '',
        lastUpdated: 'all',
        customDateValue: '',
        priceMin: '',
        priceMax: '',
        quantity: '',
    };
    const [filters, setFilters] = useState(defaultFilters);
    const [supplierOptions, setSupplierOptions] = useState([]);

    // Load supplier list once
    useEffect(() => {
        fetch(`${API_BASE_URL}${API_INVENTORY}`)
            .then((r) => r.json())
            .then((data) => {
                const labs = Array.from(
                    new Set(data.map((i) => i.specifications?.laboratory).filter(Boolean))
                );
                setSupplierOptions(labs);
            })
            .catch(console.error);
    }, []);

    const handleChange = (field) => (e) => {
        setFilters((prev) => ({
            ...prev,
            [field]: e.target.value,
            // clear customDateValue if switching away
            ...(field === 'lastUpdated' && e.target.value !== 'customDate'
                ? { customDateValue: '' }
                : {}),
        }));
    };

    const handleClear = () => {
        setFilters(defaultFilters);
        onApply(defaultFilters);
        onClose();
    };

    const handleApply = () => {
        onApply(filters);
        onClose();
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: 500, p: 2, position: 'relative' }}>
                {/* Close button in top right corner */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1,
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Typography variant="h6" gutterBottom sx={{ pr: 5 }}>
                    Filtrar Inventario
                </Typography>

                {/* Health Center */}
                <Box mb={2}>
                    <Typography variant="subtitle2">Health Center</Typography>
                    <Select
                        fullWidth
                        value={filters.healthCenter}
                        onChange={handleChange('healthCenter')}
                        displayEmpty
                    >
                        <MenuItem value="">
                            <em>Seleccione...</em>
                        </MenuItem>
                        <MenuItem value="HC001">HC001</MenuItem>
                        <MenuItem value="HC002">HC002</MenuItem>
                    </Select>
                </Box>

                {/* Medicine Category */}
                <Box mb={2}>
                    <Typography variant="subtitle2">Medicine Category</Typography>
                    <Select
                        fullWidth
                        value={filters.medicineCategory}
                        onChange={handleChange('medicineCategory')}
                        displayEmpty
                    >
                        <MenuItem value="">
                            <em>Seleccione...</em>
                        </MenuItem>
                        <MenuItem value="antibiotics">Antibiotics</MenuItem>
                        <MenuItem value="analgesics">Analgesics</MenuItem>
                    </Select>
                </Box>

                {/* Stock Status */}
                <Box mb={2}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Stock Status</FormLabel>
                        <RadioGroup
                            value={filters.stockStatus}
                            onChange={handleChange('stockStatus')}
                        >
                            <FormControlLabel value="all" control={<Radio />} label="All" />
                            <FormControlLabel
                                value="inStock"
                                control={<Radio />}
                                label="In Stock"
                            />
                            <FormControlLabel
                                value="lowStock"
                                control={<Radio />}
                                label="Low Stock"
                            />
                            <FormControlLabel
                                value="outOfStock"
                                control={<Radio />}
                                label="Out of Stock"
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>

                {/* Expiry Date */}
                <Box mb={2}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Expiry Date</FormLabel>
                        <RadioGroup
                            value={filters.expiryDate}
                            onChange={handleChange('expiryDate')}
                        >
                            <FormControlLabel value="all" control={<Radio />} label="All" />
                            <FormControlLabel
                                value="30days"
                                control={<Radio />}
                                label="Expires in 30 days"
                            />
                            <FormControlLabel
                                value="60days"
                                control={<Radio />}
                                label="Expires in 60 days"
                            />
                            <FormControlLabel
                                value="90days"
                                control={<Radio />}
                                label="Expires in 90 days"
                            />
                            <FormControlLabel
                                value="expired"
                                control={<Radio />}
                                label="Expired"
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>

                {/* Supplier */}
                <Box mb={2}>
                    <Typography variant="subtitle2">Supplier (Laboratory)</Typography>
                    <Select
                        fullWidth
                        value={filters.supplier}
                        onChange={handleChange('supplier')}
                        displayEmpty
                    >
                        <MenuItem value="">
                            <em>Seleccione...</em>
                        </MenuItem>
                        {supplierOptions.map((lab, idx) => (
                            <MenuItem key={idx} value={lab}>
                                {lab}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>

                {/* Last Updated */}
                <Box mb={2}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Last Updated</FormLabel>
                        <RadioGroup
                            value={filters.lastUpdated}
                            onChange={handleChange('lastUpdated')}
                        >
                            <FormControlLabel value="all" control={<Radio />} label="All" />
                            <FormControlLabel value="today" control={<Radio />} label="Today" />
                            <FormControlLabel
                                value="lastWeek"
                                control={<Radio />}
                                label="Last Week"
                            />
                            <FormControlLabel
                                value="lastMonth"
                                control={<Radio />}
                                label="Last Month"
                            />
                            <FormControlLabel
                                value="customDate"
                                control={<Radio />}
                                label="Custom Date"
                            />
                        </RadioGroup>
                    </FormControl>
                    {filters.lastUpdated === 'customDate' && (
                        <TextField
                            type="date"
                            fullWidth
                            sx={{ mt: 1 }}
                            value={filters.customDateValue}
                            onChange={handleChange('customDateValue')}
                        />
                    )}
                </Box>

                {/* Price Range */}
                <Box mb={2}>
                    <Typography variant="subtitle2">Price Range</Typography>
                    <Box display="flex" gap={2}>
                        <TextField
                            label="Min"
                            type="number"
                            fullWidth
                            value={filters.priceMin}
                            onChange={handleChange('priceMin')}
                        />
                        <TextField
                            label="Max"
                            type="number"
                            fullWidth
                            value={filters.priceMax}
                            onChange={handleChange('priceMax')}
                        />
                    </Box>
                </Box>

                {/* Quantity */}
                <Box mb={2}>
                    <Typography variant="subtitle2">Quantity</Typography>
                    <TextField
                        type="number"
                        fullWidth
                        placeholder="Enter exact quantity"
                        value={filters.quantity}
                        onChange={handleChange('quantity')}
                    />
                </Box>

                {/* Buttons */}
                <Grid container spacing={2} justifyContent="flex-end">
                    <Grid item>
                        <Button onClick={handleClear} variant="outlined" color="secondary">
                            Clear
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button onClick={handleApply} variant="contained" color="primary">
                            Apply
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Drawer>
    );
}